<template>
    <b-container class="slim-content">
        <b-card no-body class="returns-card py-4 px-md-5 py-md-5 mb-5 mobile-padding">
            <b-row>
                <b-col cols="10" md="10">
                    <Stepper @click="currentStep = $event;" :currentStep="currentStep" :finalStep="4"></Stepper>
                </b-col>
                <b-col cols="2" md="2">
                    <QuestionButton :currentStep="currentStep"></QuestionButton>
                </b-col>
            </b-row>
            <b-row class="mb-2">
                <b-col v-if="currentStep === 1">
                    <h1>{{ $t('step' + currentStep + '.title') }}</h1>
                    <p v-html="$t('step' + currentStep + '.supportText')"/>
                    <p v-html="$t('step' + currentStep + '.additionalSupportText')"/>
                </b-col>
                <b-col v-else>
                    <h1>{{ $t('step' + currentStep + '.title' + capitalizeFirstLetter(this.formData.complaintType.selected)) }}</h1>
                    <p v-html="$t('step' + currentStep + '.supportText' + capitalizeFirstLetter(this.formData.complaintType.selected))"></p>
                </b-col>
            </b-row>

            <b-form :key="currentStep">
                <section class="steps">

                    <!--

                    STEP 1

                    --->
                    <section class="step1" v-if="currentStep === 1">
                        <b-form-group
                            :label="$t('step1.emailLabel')"
                            label-for="input-email"
                        >
                            <b-form-input
                                id="input-email"
                                v-model="formData.email"
                                type="email"
                                :placeholder="$t('step1.emailPlaceholder')"
                                :state="validateEmail(formData.email)"
                                required
                            ></b-form-input>
                        </b-form-group>

                        <b-form-group
                            :label="$t('step1.orderNumberLabel')"
                            label-for="input-order-number"
                        >
                            <b-form-input
                                id="input-order-number"
                                v-model="formData.orderNumber"
                                type="email"
                                :placeholder="$t('step1.orderNumberPlaceholder')"
                                required
                            ></b-form-input>
                        </b-form-group>

                        <b-form-group v-if="false">
                            <b-form-radio-group
                                id="radio-slots"
                                v-model="formData.complaintType.selected"
                                :options="formData.complaintType.options"
                                required
                                name="radio-options-slots"
                            />
                        </b-form-group>

                        <ErrorMessages :scroll-to-top="scrollToTop" :validation-errors="validationErrors"/>

                        <b-row>
                            <b-col md="6">
                            </b-col>
                            <b-col md="6">
                                <b-button variant="primary"
                                          style="width: 100%"
                                          :disabled="(formData.complaintType.selected === null)"
                                          @click="fetchOrderData"
                                          class="btn-action"
                                >
                                    <b-icon-journal-check></b-icon-journal-check>
                                    {{ $t('step1.createNewButton') }}
                                </b-button>
                            </b-col>
                        </b-row>

                    </section>

                    <!--

                    STEP 2

                    --->
                    <section class="step2" v-if="currentStep === 2">
                        <ItemList :form-data="formData"
                                  :order-data="orderData"
                                  :is-return-case="isReturnCase()"
                                  :reason-options="reasonOptions"
                                  @update="orderData.items = [...$event.items]"
                        />

                        <FreightOptions v-if="isReturnCase()"
                                        :form-data="formData"
                                        :freight-options="freightOptions"
                                        :freight-store-options="freightStoreOptions"
                        />

                        <StoreInformation v-if="isReturnCase()" :check-is-return-to-store="checkIsReturnToStore"
                                          :selected-store-config="selectedStoreConfig"
                                          :selected-store-name="selectedStoreName"/>

                        <div v-if="isComplaintCase()">
                            <b-form-file v-model="formData.images" accept="image/*" :multiple="true"></b-form-file>
                        </div>

                        <ContactInformation :order-data="orderData" :can-edit="true" @save="contactSaved"/>
                        <ErrorMessages :scroll-to-top="scrollToTop" :validation-errors="validationErrors"/>
                        <StepperButtons
                            next-step-text="step2.nextStepButton"
                            :next-step="validateAndStepToThree"
                            :previous-step="previousStep"
                        />
                    </section>

                    <!--

                    STEP 3

                    --->
                    <section class="step3" v-if="currentStep === 3">
                        <ConfirmList class="mb-4"
                                     :form-data="formData"
                                     :order-data="orderData"
                                     :reason-options="reasonOptions">
                        </ConfirmList>

                        <div v-if="isReturnCase()">
                            <ItemHeaderRow>
                                <b-col>
                                    {{ $t('step3.freightAndReturnTitle') }}
                                </b-col>
                            </ItemHeaderRow>
                            <b-card bg-variant="white">
                                <b-row>
                                    <b-col>
                                        {{ freightOptions.find(item => item.value === formData.freightSelected).text }}
                                    </b-col>
                                    <b-col align="right">
                                        {{ this.resolveFee(this.formData.freightSelected).text }}
                                    </b-col>
                                </b-row>
                                <hr v-if="checkIsReturnToStore()">
                                <StoreInformation :check-is-return-to-store="checkIsReturnToStore"
                                                  :selected-store-config="selectedStoreConfig"
                                                  :selected-store-name="selectedStoreName"
                                                  :no-body="true"
                                />
                            </b-card>

                            <b-row class="mt-3">
                                <b-col>
                                    <b>{{ $t('step3.sumRepaymentText') }}</b>
                                </b-col>
                                <b-col align="right">
                                    {{ this.sumAfterFreight() }}
                                </b-col>
                            </b-row>
                        </div>

                        <ContactInformation :order-data="orderData"/>

                        <StepperButtons
                            next-step-text="common.confirm"
                            :next-step="finalStep"
                            :previous-step="previousStep"
                        />
                    </section>

                    <!--

                    STEP 4

                    --->
                    <section class="step4" v-if="currentStep === 4">
                        <p>{{ $t('step4.caseNumberTitle') }} <strong>{{ complaintResponse.caseNumber }}</strong></p>

                        <!-- Postnord -->
                        <b-card bg-variant="white"
                                v-if="isReturnCase() && !checkIsReturnToStore()"
                                class="postnord-returns"
                        >
                            <ItemHeaderRow>
                                <b-col>
                                    {{ $t('step4.whatHappensNow') }}
                                </b-col>
                            </ItemHeaderRow>

                            <ol class="postnord-steps">
                                <li v-if="$t('step4.postnordPoint1').length" v-html="$t('step4.postnordPoint1')"></li>
                                <li v-if="$t('step4.postnordPoint2').length" v-html="$t('step4.postnordPoint2')"></li>
                                <li v-if="$t('step4.postnordPoint3').length" v-html="$t('step4.postnordPoint3')"></li>
                                <li v-if="$t('step4.postnordPoint4').length" v-html="$t('step4.postnordPoint4')"></li>
                                <li v-if="$t('step4.postnordPoint5').length" v-html="$t('step4.postnordPoint5')"></li>
                            </ol>

                            <b-row class="pdf-code-container" v-if="!showQrCode()">
                                <b-col cols="12" md="6">
                                    <ItemHeaderRow>
                                        <b-col>
                                            {{ $t('step4.yourPdfDownload') }}
                                        </b-col>
                                    </ItemHeaderRow>
                                    <ul style="list-style: inside;">
                                        <li v-for="(pdf, index) in complaintResponse.printout.filter(item => item.printout.labelFormat === 'PDF')" :key="'p-' + index">
                                            <a download
                                               target="_blank"
                                               :href="`data:application/pdf;base64,${pdf.printout.data}`"
                                            >
                                                {{ $t('step4.downloadPdf') }} (nr {{ index + 1 }})
                                            </a>
                                        </li>
                                    </ul>
                                </b-col>
                            </b-row>

                            <div  v-if="showQrCode()">
                                <b-row class="qr-code-container" v-for="(code, cIndex) in complaintResponse.printout.filter(item => item.printout.labelFormat === 'PNG')" :key="'c-' + cIndex">
                                    <b-col cols="12" md="6">
                                        <img alt="" :src="`data:application/png;base64,${code.printout.data}`"/>
                                        <p style="text-align: center;">{{ code.printout.dataValue }}</p>
                                        <hr>
                                    </b-col>
                                </b-row>
                            </div>
                        </b-card>

                        <b-card bg-variant="white" v-if="isReturnCase() && checkIsReturnToStore()">
                            <ItemHeaderRow>
                                <b-col>
                                    {{ $t('step4.whatHappensNow') }}
                                </b-col>
                            </ItemHeaderRow>
                            <section class="return-to-store-info">
                                <ol class="return-to-store-steps">
                                    <li v-html="$t('step4.storePoint1')"></li>
                                    <li v-html="$t('step4.storePoint2')"></li>
                                    <li v-html="$t('step4.storePoint3')"></li>
                                </ol>
                                <strong>{{ $t('step4.storeWelcomeMessage') }}</strong>
                            </section>
                        </b-card>
                    </section>
                </section>
            </b-form>
            <div v-if="loading" class="loading-overlay">
                <b-spinner label="Loading..."></b-spinner>
            </div>
        </b-card>
        <Faq></Faq>
    </b-container>
</template>
<script>
import Stepper from "../Widget/Stepper";
import QuestionButton from "../Buttons/QuestionButton";
import { BIconJournalCheck } from "bootstrap-vue";
import ItemList from "./ItemList";
import StepperButtons from "./StepperButtons";
import FreightOptions from "./FreightOptions";
import HttpClient from "../../services/HttpClient";
import ConfirmList from "./ConfirmList";
import ErrorMessages from "./ErrorMessages";
import ContactInformation from "./ContactInformation";
import Faq from "./Faq";
import { capitalizeFirstLetter, formatCurrency } from "../../services/Common";
import ItemHeaderRow from "./Item/ItemHeaderRow";
import StoreInformation from "./StoreInformation";


export default {
    name: 'MainPage',
    components: {
        StoreInformation,
        ItemHeaderRow,
        ContactInformation,
        ErrorMessages,
        FreightOptions,
        StepperButtons,
        ItemList,
        Stepper,
        QuestionButton,
        BIconJournalCheck,
        ConfirmList,
        Faq
    },
    data() {
        return {
            currentStep: 1,
            loading: false,
            freightOptions: [],
            reasonOptions: [
                { value: 'regret_purchase', text: this.$t('step2.reason.regretPurchase') },
                { value: 'does_not_meet_expectations', text: this.$t('step2.reason.doesNotMeetExpectations') },
                {
                    value: 'does_not_match_description',
                    text: this.$t('step2.reason.doesNotMatchDescription')
                },
                { value: 'other', text: this.$t('step2.reason.other') },
            ],
            validationErrors: [],
            freightStoreOptions: [],
            formData: {
                packageQuantity: 1,
                email: null,
                orderNumber: null,
                freightSelected: 'postnord',
                storeSelected: null,
                images: [],
                complaintType: {
                    selected: 'return',
                    options: [
                        { text: this.$t('step1.formTypeReturn'), value: 'return' },
                        { text: this.$t('step1.formTypeComplaint'), value: 'complaint' }
                    ]
                },
                itemsSelected: [],
            },
            orderConfig: {
                shipping: null
            },
            orderData: {
                email: null,
                items: [],
                deliveryContact: {
                    address: {
                        phone: null,
                        name: null,
                        city: null,
                        country: null,
                        street1: null,
                        street2: null,
                        zip: null
                    },
                }
            },
            complaintResponse: {}
        };
    },
    methods: {
        capitalizeFirstLetter,
        formatCurrency,
        showQrCode() {
            return this.orderData?.deliveryContact?.address?.country?.toUpperCase() === 'SE';
        },
        isReturnCase() {
            return this.formData.complaintType.selected === 'return';
        },
        isComplaintCase() {
            return this.formData.complaintType.selected === 'complaint';
        },
        scrollToTop() {
            window.scrollTo(0, 0)
        },
        freightOptionsData() {
            return Object.entries(this.orderConfig.shipping).filter(([ key, item ]) => item.active).map(([ key, item ]) => {
                return {
                    value: key,
                    text: `${this.$t(item.translationCode)} (${this.resolveFee(key).text})`
                };
            });
        },
        validateEmail(email) {
            if (email === null) {
                return null;
            }

            const re = /\S+@\S+\.\S+/;
            return re.test(email);
        },
        async convertToBase64(file) {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => {
                    resolve(reader.result);
                };
                reader.onerror = reject;
                reader.readAsDataURL(file);
            });
        },
        resolveFee(shippingCode) {
            if (!this.orderConfig || !this.orderData.items) {
                return '';
            }

            const shippingProvider = this.orderConfig.shipping[shippingCode];
            const currency = this.orderData.items[0].priceCurrency;
            const fee = shippingProvider.fee[currency];
            return {
                currency: currency,
                fee: fee,
                text: this.formatCurrency(currency, shippingProvider.fee[currency])
            };
        },
        getItemBySku(sku) {
            return this.orderData.items.find(item => item.sku === sku);
        },
        sumAfterFreight() {
            const freight = this.resolveFee(this.formData.freightSelected);

            const sum = this.formData.itemsSelected.map(sku => {
                let item = this.getItemBySku(sku);
                return item.priceIncludingTax * item.selectedQuantity;
            }).reduce((prev, next) => prev + next);

            return this.formatCurrency(freight.currency, sum - freight.fee);
        },
        async createComplaint() {
            this.loading = true;
            const { address, phone } = this.orderData.deliveryContact;
            let data = {
                ...this.formData,
                addressName: address.name,
                addressPhone: phone,
                addressCity: address.city,
                addressCountry: address.country,
                addressStreet1: address.street1,
                addressStreet2: address.street2,
                addressZip: address.zip,
                items: this.formData.itemsSelected.map(sku => {
                    let item = this.getItemBySku(sku);
                    return {
                        comment: item.comment,
                        orderItemId: item.orderItemId,
                        selectedQuantity: item.selectedQuantity,
                        returnReasonType: item.returnReasonType,
                        sku: item.sku,
                        shipments: item.shipments.shipments,
                    };
                }),
                complaintType: this.formData.complaintType.selected
            };

            if (this.isComplaintCase()) {
                data.images = await Promise.all(this.formData.images.map(async file => {
                    return { base64: await this.convertToBase64(file) };
                }));
            }

            const response = await HttpClient.postData('rma/create', data);
            const responseData = await response.json();

            if (responseData.status === 'success') {
                this.loading = false;
                this.nextStep();
                this.complaintResponse = responseData;
            } else {
                if (responseData.errors?.order_id?.length) {
                    await this.$bvModal.msgBoxOk(
                        this.$t('validate.orderHasExistingReturn'), {
                            title: 'Ärende existerar redan',
                            centered: true
                        });
                } else {
                    await this.$bvModal.msgBoxOk(
                        'Ett tekniskt fel uppstod var vänlig och försök igen om en liten stund.', {
                            title: 'Tekniskt fel uppstod!',
                            centered: true
                        });
                }

                this.loading = false;
            }
        },
        isAllowedReturn(json) {
            return json.lastDeliveryDate.allowed;
        },
        isTestEnvironment() {
            return location.host.includes('stage') || location.host.includes('localhost');
        },
        fetchOrderData() {
            const data = {
                orderNumber: this.formData.orderNumber,
                email: this.formData.email,
            };

            this.validationErrors = [];
            this.loading = true;

            HttpClient.postData('rma/retrieve-order', data)
                .then(res => res.json())
                .then(json => {
                    this.loading = false;

                    if (json.status === 'order_not_delivered') {
                        this.validationErrors.push(this.$t('validate.orderIsNotDeliveredYet'));
                        return;
                    }

                    if (json.status === 'order_not_found') {
                        this.validationErrors.push(this.$t('validate.orderWasNotFound'));
                        return;
                    }

                    if (json.order.orderHasExistingReturn) {
                        this.validationErrors.push(this.$t('validate.orderHasExistingReturn'));
                        if (!this.isTestEnvironment()) {
                            return;
                        }
                    }

                    if (!this.isAllowedReturn(json) && this.isReturnCase()) {
                        this.validationErrors.push(this.$t('validate.orderIsNotValidForReturn', json.lastDeliveryDate));
                        if (!this.isTestEnvironment()) {
                            return;
                        }
                    }

                    if (json.order.hasOwnProperty('orderNumber')) {
                        this.formData.uniqueFormId = json.order.uniqueFormId;
                        this.formData.orderNumber = json.order.orderNumber;
                        this.orderData = json.order;
                        this.orderConfig = json.config;
                        this.storesConfig = json.stores;
                        this.freightOptions = this.freightOptionsData();
                        this.freightStoreOptions = json.stores.map(item => {
                            return {
                                text: item.name,
                                value: item.id,
                            };
                        });
                        this.orderData.items = this.orderData.items.map(item => {
                            item.selectedQuantity = 0;
                            item.returnReasonType = null;
                            item.comment = null;
                            item.imageUrl = '/images/product/' + item.uuid + '/main/square-500';
                            item.validationErrors = [];
                            return item;
                        });
                        this.nextStep();
                    }

                })
                .catch(res => {
                    this.loading = false;
                    console.log(res);
                });
        },
        validateAndStepToThree() {
            this.validationErrors = [];

            if (this.formData.itemsSelected.length < 1) {
                this.validationErrors.push(this.$t('validate.mustHaveSelections'))
            }

            if (this.formData.freightSelected === 'return_to_store' && this.formData.storeSelected === null) {
                this.validationErrors.push(this.$t('validate.mustHaveStoreSelectedOnReturnToStore'));
            }

            const notValid = this.formData.itemsSelected.filter(sku => {
                const item = this.orderData.items.find(item => item.sku === sku);
                console.log(sku);
                return !this.validateItem(item);
            });

            if (notValid.length + this.validationErrors.length) {
                if (notValid.length) {
                    this.validationErrors.push(this.$t('validate.mustHaveNoErrors', { length: notValid.length }))
                }

                this.orderData = {
                    ...this.orderData
                };

                return;
            }

            this.nextStep();
        },
        validateItem(item) {
            item.validationErrors = [];
            const mustHaveQuantity = item.selectedQuantity > 0;
            const mustHaveReason = item.returnReasonType !== null;
            const mustHaveQuantityBelowMax = item.selectedQuantity <= item.quantity;

            if (!mustHaveQuantity) {
                item.validationErrors.push(this.$t('validate.mustHaveQuantity'));
            }

            if (!mustHaveReason) {
                item.validationErrors.push(this.$t('validate.mustHaveReason'));
            }

            if (!mustHaveQuantityBelowMax) {
                item.validationErrors.push(this.$t('validate.mustHaveQuantityBelowMax', item));
            }

            return mustHaveQuantity &&
                mustHaveReason && mustHaveQuantityBelowMax;
        },
        nextStep() {
            this.currentStep = this.currentStep + 1;
            this.scrollToTop();
        },
        previousStep() {
            this.currentStep = this.currentStep - 1;
            this.scrollToTop();
        },
        finalStep() {
            this.loading = true;
            this.scrollToTop();
            this.createComplaint();
        },
        checkIsReturnToStore() {
            return !!(this.formData.storeSelected && this.formData.freightSelected === 'return_to_store');
        },
        selectedStoreConfig() {
            return this.storesConfig.find(item => {
                return item.id === this.formData.storeSelected;
            });
        },
        selectedStoreName() {
            const store = this.freightStoreOptions.find(item => item.value === this.formData.storeSelected);
            return store.text;
        },
        contactSaved(deliveryContact) {
            this.orderData.deliveryContact = deliveryContact;
        }
    }
}
</script>
<style scoped>
.slim-content {
    max-width: 750px;
}

.returns-card {
    background: #f2f2f2;
    border: none;
}

.error-card .card-body {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.mobile-padding {
    padding: 1rem;
}

.loading-overlay {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    background: rgba(255, 255, 255, 0);

    display: flex;
    justify-content: center;
    align-items: center;
    animation: fadeIn 0.3s forwards;
}

.qr-code-container {
    justify-content: center;
}

.postnord-steps {
    padding: 1rem;
}

.postnord-steps li {
    margin-bottom: 0.5rem;
}

.return-to-store-steps {
    padding: 1rem;
}

.return-to-store-steps li {
    margin-bottom: 0.5rem;
}

.postnord-returns .card-body {
    padding: 2.5rem;
}

.postnord-returns img {
    max-width: 100%;
}

@keyframes fadeIn {
    0% {
        background: rgba(255, 255, 255, 0);
    }

    100% {
        background: rgba(255, 255, 255, 0.5);
    }
}
</style>
